import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { format } from 'date-fns/format';
import { useMemo } from 'react';
import { DisabledStore } from '../../components/disabled-store';
import { EmptyState } from '../../components/empty-state';
import { Tooltip } from '../../components/popover/tooltip';
import { StatusCell } from '../../components/status/status-cell';
import type { Order, OrderDeliveryState } from '../../data/order';
import { caseCount, orderDeliveryState, orderTotalCost, unitCount } from '../../data/order';
import type { Product } from '../../data/product';
import type { Store } from '../../data/store';
import type { Vendor } from '../../data/vendor';
import { shortCurrencyFormatter } from '../../helpers/format-currency';
import { useMaybeCreateOrder, useOrders } from '../../hooks/orders';
import { useProducts } from '../../hooks/products';
import { useVendors } from '../../hooks/vendors';
import { storeLayout } from '../../router';
import { getSuggestedQuantities } from './edit/utils';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

function NewOrderMenu({
  vendors,
  createOrder,
}: {
  vendors: Vendor[];
  createOrder: (vendor: Vendor) => Promise<void>;
}) {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <MenuButton className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-primary-600">
          New Order
          <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
        </MenuButton>
      </div>

      <MenuItems
        transition
        className="absolute right-0 z-popover mt-2 w-56 origin-top-right rounded-md bg-popover shadow-popover focus:outline-hidden transition ease-out duration-100 data-closed:scale-95 data-closed:opacity-0 data-leave:duration-75 data-leave:ease-in"
      >
        <div className="py-1">
          {vendors.map((vendor) => (
            <MenuItem key={vendor.vendor_id}>
              {({ focus }) => (
                <button
                  onClick={() => createOrder(vendor)}
                  className={classNames(
                    focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                    'block w-full text-left px-4 py-2 text-sm'
                  )}
                  type="button"
                >
                  {vendor.name}
                </button>
              )}
            </MenuItem>
          ))}
        </div>
      </MenuItems>
    </Menu>
  );
}

const VendorName = ({ id, vendors }: { id: string; vendors: Vendor[] }) => {
  const vendor = useMemo(() => {
    return vendors.find((v) => v.vendor_id === id);
  }, [id, vendors]);

  return vendor?.name;
};

function DeliveryIndicator({ state }: { state: OrderDeliveryState }) {
  if (state === 'clean') {
    return null;
  }

  // 'failed' = every rep bounced (red); 'partial'/'unknown' = warn (amber).
  const failed = state === 'failed';

  return (
    <Tooltip
      content={
        failed
          ? 'This order could not be delivered to the vendor. Every address bounced.'
          : "Some recipients didn't receive this order."
      }
    >
      <StatusCell variant={failed ? 'danger' : 'attention'} className="gap-1">
        {failed ? (
          <ExclamationCircleIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        ) : (
          <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        )}
        {failed ? 'Send failed' : 'Sending issue'}
      </StatusCell>
    </Tooltip>
  );
}

function OrderCard({
  store,
  vendors,
  products,
  order,
  route,
}: {
  store: Store;
  vendors: Vendor[];
  products: Product[];
  order: Order;
  route: '/edit' | '/summary';
}) {
  const populatedOrder = useMemo(() => {
    if (order.status !== 'draft') {
      return order;
    }

    // Populate the order with suggested quantities
    const suggestedItems = products
      .filter((p) => p.vendor_id === order.vendor_id)
      .map((p) => {
        const existingItem = order.items.find((item) => item.product.id === p.id);
        const suggestedQuantities = getSuggestedQuantities(p, order.options);

        return {
          ...existingItem,
          ...suggestedQuantities,
          product: p,
        };
      });

    return {
      ...order,
      items: suggestedItems,
    };
  }, [order, products]);

  const totalCases = useMemo(() => caseCount(populatedOrder.items), [populatedOrder.items]);
  const totalUnits = useMemo(() => unitCount(populatedOrder.items), [populatedOrder.items]);
  const totalCost = useMemo(() => orderTotalCost(populatedOrder.items), [populatedOrder.items]);
  const deliveryState = orderDeliveryState(order);

  return (
    <li>
      <Link
        to={
          route === '/edit'
            ? '/stores/$storeId/orders/$orderId/edit'
            : '/stores/$storeId/orders/$orderId/summary'
        }
        params={{ storeId: store.id, orderId: order.id }}
        className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 p-5 sm:flex-nowrap hover:bg-gray-50"
      >
        <div>
          <div className="flex items-center gap-x-2">
            <p className="text-sm font-semibold leading-6 text-gray-900">
              <VendorName id={order.vendor_id} vendors={vendors} />
            </p>
            <DeliveryIndicator state={deliveryState} />
          </div>
          <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
            <p>Last Updated {format(order.updated_at, 'MM/dd/yyyy hh:mmaa')}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-x-4">
          <div className="hidden sm:flex sm:flex-col sm:items-end">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1 w-full">
              <span className="w-16 text-right">{totalCases} cases</span>
              <span className="w-16 text-right">{totalUnits} units</span>
              <span className="w-20 text-right font-medium">
                {shortCurrencyFormatter(totalCost)}
              </span>
            </div>
            <p className="text-sm leading-6 text-gray-900">{order.status}</p>
          </div>
          <ChevronRightIcon className="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
        </div>
      </Link>
    </li>
  );
}

function OrderList({
  store,
  vendors,
  orders,
  route,
  products,
}: {
  store: Store;
  vendors: Vendor[];
  orders: Order[];
  route: '/edit' | '/summary';
  products: Product[];
}) {
  return (
    <ul className="divide-y divide-gray-100">
      {orders.map((order) => (
        <OrderCard
          key={order.id}
          store={store}
          vendors={vendors}
          products={products}
          order={order}
          route={route}
        />
      ))}
      {orders.length === 0 && (
        <EmptyState
          icon={<ClipboardDocumentListIcon className="size-8" />}
          title={route === '/edit' ? 'No open orders' : 'No order history yet'}
        />
      )}
    </ul>
  );
}

export function Orders() {
  const { store } = storeLayout.useRouteContext();
  const { orders } = useOrders();
  const { vendors } = useVendors();
  const { products } = useProducts();
  const enabledVendors = useMemo(() => {
    return vendors.filter((v) => !v.disabled);
  }, [vendors]);
  const maybeCreateOrder = useMaybeCreateOrder();

  if (!store.enabled) {
    return <DisabledStore />;
  }

  return (
    <div className="h-full w-full flex flex-col">
      <div className="max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-3xl font-semibold text-gray-900">Open Orders</h1>
          <NewOrderMenu
            vendors={enabledVendors}
            createOrder={(vendor) => maybeCreateOrder(store, vendor)}
          />
        </div>
        <OrderList
          store={store}
          vendors={vendors}
          products={products}
          orders={orders.filter((order) => order.status === 'draft')}
          route="/edit"
        />
        <h1 className="text-3xl font-semibold text-gray-900 px-4 py-3">History</h1>
        <OrderList
          store={store}
          vendors={vendors}
          products={products}
          orders={orders.filter((order) => order.status !== 'draft')}
          route="/summary"
        />
      </div>
    </div>
  );
}
